<template>
  <section :class="$style.screen">
    <div :class="$style.card">
      <div :class="$style.brand">
        <div :class="$style.logo">
          <img v-if="department?.logo" :src="department.logo" alt="" />
          <div v-else :class="$style.code">
            {{ department?.short_code || "TS" }}
          </div>
        </div>

        <div :class="$style.info">
          <p v-if="department?.title" :class="$style.departmentTitle">
            {{ department.title }}
          </p>
          <p v-if="department?.address" :class="$style.address">
            {{ department.address }}
          </p>
        </div>
      </div>

      <div :class="$style.contacts">
        <h1 :class="$style.title">
          Запись на сервис в данный момент недоступна
        </h1>
        <a
          v-if="department?.phone_number"
          :href="`tel:${department.phone_number}`"
          :class="$style.phone"
        >
          {{ department.phone_number }}
        </a>

        <div v-if="socialLinks.length" :class="$style.socials">
          <a
            v-for="item in socialLinks"
            :key="item.key"
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
            :class="$style.social"
            :aria-label="item.label"
          >
            <img :src="item.icon" :class="$style[`icon-${item.key}`]" alt="" />
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ServiceUnavailableScreen",
  props: {
    department: {
      type: Object,
      default: null,
    },
  },
  computed: {
    socialLinks() {
      const department = this.department || {};
      return [
        {
          key: "vk",
          label: "VK",
          icon: "/figma/step1-vk.svg",
          url: this.normalizeSocialUrl(department.vk_link),
        },
        {
          key: "telegram",
          label: "Telegram",
          icon: "/figma/step1-tg.svg",
          url: this.normalizeTelegramUrl(department.telegram_link),
        },
        {
          key: "whatsapp",
          label: "WhatsApp",
          icon: "/figma/step1-wa.svg",
          url: this.normalizeWhatsAppUrl(department.whatsapp_link),
        },
        {
          key: "max",
          label: "Max",
          icon: "/figma/step1-max.svg",
          url: this.normalizeSocialUrl(department.max_link),
        },
      ].filter((item) => item.url);
    },
  },
  methods: {
    normalizeSocialUrl(value) {
      const normalized = String(value || "").trim();
      if (!normalized) {
        return "";
      }

      if (/^https?:\/\//i.test(normalized)) {
        return normalized;
      }

      return `https://${normalized.replace(/^\/+/, "")}`;
    },
    normalizeTelegramUrl(value) {
      const normalized = String(value || "").trim();
      if (!normalized) {
        return "";
      }

      if (/^https?:\/\//i.test(normalized)) {
        return normalized;
      }

      if (normalized.startsWith("@")) {
        return `https://t.me/${normalized.slice(1)}`;
      }

      return this.normalizeSocialUrl(normalized);
    },
    normalizeWhatsAppUrl(value) {
      const normalized = String(value || "").trim();
      if (!normalized) {
        return "";
      }

      if (/^https?:\/\//i.test(normalized)) {
        return normalized;
      }

      const digits = normalized.replace(/\D/g, "");
      if (digits) {
        return `https://wa.me/${digits}`;
      }

      return this.normalizeSocialUrl(normalized);
    },
  },
};
</script>

<style lang="scss" module>
.screen {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  .card {
    width: 100%;
    max-width: 33rem;
    padding: 1.5rem;
    border: 1px solid $bg-secondary;
    border-radius: 1rem;
    background-color: $bg-white;
    box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.08);
  }
}

.card {
  .brand {
    display: flex;
    align-items: center;
    gap: 1rem;

    .logo {
      width: 3.5rem;
      height: 3.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      .code {
        width: 3.5rem;
        height: 3.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid $bg-separator;
        border-radius: 0.5rem;
        background-color: $bg-white;
        font-size: 1.25rem;
        font-weight: 600;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        border-radius: 0.5rem;
      }
    }

    .info {
      min-width: 0;

      .departmentTitle {
        margin: 0 0 0.125rem;
        color: $t-primary;
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.4;
      }

      .address {
        margin: 0.25rem 0 0;
        color: $t-secondary;
        font-size: 0.875rem;
        line-height: 1.4;
      }
    }
  }

  .contacts {
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    .title {
      margin: 0;
      color: $t-primary;
      font-size: 1.25rem;
      font-weight: 600;
      line-height: 1.3;
    }
    .phone {
      display: inline-flex;
      align-items: center;
      min-height: 2.5rem;
      padding: 0.5rem 1rem;
      border: 1px solid $t-primary;
      border-radius: 6.25rem;
      background-color: $bg-white;
      color: $t-primary;
      font-size: 1rem;
      line-height: 1.5;
      white-space: nowrap;
    }

    .socials {
      display: flex;
      align-items: center;
      gap: 0.375rem;

      .social {
        width: 2.5rem;
        height: 2.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid $t-primary;
        border-radius: 50%;
        background-color: $bg-white;
        flex-shrink: 0;

        img {
          object-fit: contain;
          display: block;
        }
      }

      .icon-vk,
      .icon-whatsapp,
      .icon-max {
        width: 1.25rem;
        height: 1.25rem;
      }

      .icon-telegram {
        width: 1.2275rem;
        height: 1rem;
      }
    }
  }
}
</style>
